import useTitanApp from "@/hooks/useTitanApp";
import { usePagination } from "@/components/ui/table-pagination";
import {
  setShowMultipleVaccineCertificatePreview,
  setMultipleVaccinationImage,
} from "@/dispatchers/patientDispatchers";
import { getPharmacyContactDetails } from "@/dispatchers/pharmacyServiceDispatchers";
import { GenerateMultipleVaccinationImage } from "@/components/PatientDashboardV2/components/GenerateMultipleVaccinationImage";
import { Item } from "@radix-ui/react-select";
import { Route } from "lucide-react";
import {
  setVaccinePdfDownloadData
} from "@/dispatchers/patientPrescriptionDispatcher";

export interface Prescription {
  id: number;
  number: string;
  date: string;
  rawDate: string;
  status: "Pending" | "Approved" | "Rejected";
  medication: string;
  fileResponse: "";
  prescriptionId: string;
  declineComment: string;
  declineReason: string;
}

export interface MedicationHistoryEntry {
  id: string;
  medicationName: string;
  dose: string;
  type: "medication" | "vaccination";
  status: "dispensed" | "administered";
  date: string;
  administeredBy?: string;
  batchNumber?: string;
  site?: string;
  notes?: string;
}

export const prescriptionStatusOptions = [
  { value: "Pending", label: "Pending", color: "orange" },
  { value: "Ready", label: "Ready", color: "blue" },
  { value: "Dispensed", label: "Dispensed", color: "green" },
  { value: "Cancelled", label: "Cancelled", color: "red" },
] as const;

const prescriptionActionStatus = (status: number): Prescription["status"] => {
  switch (status) {
    case 1:
      return "Pending";
    case 2:
      return "Approved";
    case 3:
      return "Rejected";
    default:
      return "Pending";
  }
};

export const generatePrescriptionNumber = (): string => {
  const timestamp = Date.now();
  const randomSuffix = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0");
  return `RX #${timestamp.toString().slice(-4)}${randomSuffix}`;
};

export const mapConsultationsToPrescriptions = (
  patientConsultations: any[],
): Prescription[] => {
  if (!Array.isArray(patientConsultations)) return [];
  const prescriptions: Prescription[] = [];

  try {
    if (Array.isArray(patientConsultations)) {
      patientConsultations.forEach((item: any, idx: number) => {
        const formatted = `RX#${String(idx + 1).padStart(4, "0")}`;
        prescriptions.push({
          id: prescriptions.length + 1,
          number: item.actionNumber !== "" ? item.actionNumber : formatted,
          date: new Date(item.createdOn).toLocaleDateString("en-GB"),
          rawDate: item.createdOn,
          status: prescriptionActionStatus(item.status),
          medication: Array.isArray(item.drugs)
            ? item.drugs.map((d: any) => d.drugName).join(", ")
            : (item.drugs?.[0]?.drugName ?? ""),
          fileResponse: item.base64Image,
          prescriptionId: item._id,
          declineComment: item.declineComment || "",
          declineReason: item.declineReason || "",
        });
      });
    }
  } catch (err) {
    console.error("❌ Failed to parse prescription payload:", err);
  }

  return prescriptions;
};

const mapPrescriptionsToMedicationHistory = (
  prescriptions: any[],
): MedicationHistoryEntry[] => {
  if (!Array.isArray(prescriptions)) return [];

  return prescriptions.flatMap((prescription) =>
    prescription.drugs.map((drug: any) => ({
      id: drug.prescriptionItemId,
      medicationName: drug.drugName,
      dose: drug.dose,
      type: "medication",
      status: prescriptionActionStatus(prescription.status),
      date: prescription.createdOn,
      notes: drug.note || undefined,
    })),
  );
};

const mapVaccinesToMedicationHistory = (
  patientVaccinations: any[],
  selectedPatientId: string,
): MedicationHistoryEntry[] => {
  if (!Array.isArray(patientVaccinations)) return [];

  return patientVaccinations
    .filter((v) => v.patientId === selectedPatientId)
    .flatMap((record) =>
      record.vaccines.map((vaccine: any) => ({
        id: `${record._id}_${vaccine.prescriptionItemId}`,
        medicationName: vaccine.vaccineName,
        dose: vaccine.dose,
        type: "vaccination",
        status: null,
        date: record.createdOn,
        administeredBy: record.createdByName,
        batchNumber: vaccine.batchNumber,
        site: vaccine.site,
        notes: vaccine.note || undefined,
        expiryDate: vaccine.expiryDate,
        manufacturer: vaccine.manufacturer,
        route: vaccine.route,
      })),
    );
};

export const usePrescriptionsState = () => {
  const { appState, patientState } = useTitanApp();
  const { currentUser, currentPharmacy, company } = appState || {};
  const {
    patientConsultations,
    showPatientConsultationsLoader,
    patientPrescriptions,
    patientPrescriptionsLoader,
    selectedPatientDetails,
    selectedPatientId,
    patientVaccinations,
    selectedPharmacyId,
  } = patientState || {};

  const filteredPrescriptions = Array.isArray(patientPrescriptions)
    ? patientPrescriptions.filter((p) => p.patientId === selectedPatientId)
    : [];

  const prescriptions = mapConsultationsToPrescriptions(
    patientPrescriptions,
  ).sort(
    (a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime(),
  );

  const medicationHistoryFromPrescriptions =
    mapPrescriptionsToMedicationHistory(filteredPrescriptions);

  const vaccineHistory = mapVaccinesToMedicationHistory(
    patientVaccinations,
    selectedPatientId,
  );
  // Pagination setup - 5 items per page
  const pagination = usePagination(5);
  const { currentPage, itemsPerPage, handlePageChange, getPaginatedData } =
    pagination;

  // Calculate pagination
  const { paginatedData: paginatedPrescriptions, totalPages } =
    getPaginatedData(prescriptions);

  const medicationHistory: MedicationHistoryEntry[] = [
    ...medicationHistoryFromPrescriptions,
    ...vaccineHistory,
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const onGenerateVaccineCertificate = async (vaccines) => {
    const contactDetails = await getPharmacyContactDetails(selectedPharmacyId);
    const vaccineDataArray = vaccines.map((vaccine) => ({
      vaccineName: vaccine.medicationName,
      brandName: "",
      manufacturer: vaccine.manufacturer,
      dose: vaccine.dose,
      route: vaccine.route,
      site: vaccine.site || "",
      batchNumber: vaccine.batchNumber || "",
      expiryDate: vaccine.expiryDate || "",
      administeredDate: new Date(vaccine.date).toLocaleDateString("en-GB"),
    }));

    const htmlData = {
      vaccinationDate: new Date().toLocaleDateString("en-GB"),
      pharmacistDetails: currentUser,
      patientDetails: {
        title: selectedPatientDetails?.title || "",
        name: `${selectedPatientDetails?.title || ""} ${selectedPatientDetails?.foreName || ""} ${selectedPatientDetails?.surName || ""}`.trim(),
        address: selectedPatientDetails?.address || "",
        dob: selectedPatientDetails?.dob
          ? new Date(selectedPatientDetails.dob).toLocaleDateString("en-GB")
          : "",
        phone: selectedPatientDetails?.preferredContact,
        email: selectedPatientDetails?.email,
      },
      vaccinationInfo: vaccineDataArray,
      authorizationCode: Math.floor(100000 + Math.random() * 900000).toString(),
      pharmacyDetails: currentPharmacy,
      company: company,
      pharmacyContactDetails: contactDetails,
    };

    const pdfPharmacyDetails = {
      name : currentPharmacy?.teamPharmacyName,
      address : currentPharmacy?.teamPharmacyAddress,
      contactNumber: contactDetails?.phoneNumber
    };

    const pdfPharmacistDetails = {
      name : currentUser?.fullName,
      gphcNumber:currentUser?.gphcNumber
    };


    const vaccinePdfPayload = {
      patientDetails: {
        name: `${selectedPatientDetails?.title || ""} ${selectedPatientDetails?.foreName || ""} ${selectedPatientDetails?.surName || ""}`.trim(),
        address: selectedPatientDetails?.address || "",
        dob: selectedPatientDetails?.dob
          ? new Date(selectedPatientDetails.dob).toLocaleDateString("en-GB")
          : ""
      },
        vaccineData: vaccineDataArray || [],
        pharmacyDetails: pdfPharmacyDetails,
        pharmacistInformation: pdfPharmacistDetails,
      };
      

    let generatedImage = null;
    try {
      const result = await GenerateMultipleVaccinationImage({ data: htmlData });
      if (!result) throw new Error("Image generation failed");

      const { base64Image } = result;
      setMultipleVaccinationImage(base64Image);
      generatedImage = base64Image;
    } catch (error) {
      console.error(error);
    }

    setVaccinePdfDownloadData(vaccinePdfPayload);
    setShowMultipleVaccineCertificatePreview(true);
    return generatedImage;
  };

  return {
    prescriptions,
    paginatedPrescriptions,
    medicationHistory,
    totalPages,
    currentPage,
    itemsPerPage,
    handlePageChange,
    patientConsultations,
    showPatientConsultationsLoader,
    patientPrescriptionsLoader,
    onGenerateVaccineCertificate,
  };
};
